import { useCallback, useState } from 'react';
import type { IHighlight } from 'react-pdf-highlighter';
import {
  sourcesToHighlightsAndCuepoints,
  extractSectionsFromHighlightAsset,
  type SectionHighlights,
} from '@libertify.frontend/adapters';
import { hasDocumentAnimation } from '../helpers';
import type {
  CallToActions,
  Cuepoint,
  MediaProjectInstanceResponse,
} from '../types';
import { fetchJsonAsset, transformKeysToCamelCase } from '../utils';

type LegacyHighlightsAsset = {
  data: IHighlight[];
};

type NewHighlightsAsset = {
  section_highlights: SectionHighlights;
  document_id: string;
  section_map: { [key: string]: string };
  section_timestamps?: { [key: string]: number };
};

export interface UseLoadAssetsResult {
  cuepoints: Cuepoint[];
  setCuepoints: React.Dispatch<React.SetStateAction<Cuepoint[]>>;
  highlights: IHighlight[];
  setHighlights: React.Dispatch<React.SetStateAction<IHighlight[]>>;
  sections: { title: string; startTime: number }[];
  setSections: React.Dispatch<
    React.SetStateAction<{ title: string; startTime: number }[]>
  >;
  callToActions: CallToActions | undefined;
  setCallToActions: React.Dispatch<
    React.SetStateAction<CallToActions | undefined>
  >;
  loadAssets: () => Promise<boolean>;
  cleanHighlightAndCuepoints: () => void;
}

export const useLoadAssets = (
  publication: MediaProjectInstanceResponse | undefined,
): UseLoadAssetsResult => {
  const [cuepoints, setCuepoints] = useState<Cuepoint[]>([]);
  const [highlights, setHighlights] = useState<IHighlight[]>([]);
  const [sections, setSections] = useState<{ title: string; startTime: number }[]>([]);
  const [callToActions, setCallToActions] = useState<CallToActions | undefined>();

  const showDocumentAnimation = hasDocumentAnimation(publication);

  const loadAssets = useCallback(async () => {
    const cuepointAsset = publication?.document_animation?.cuepoints;
    const highlightAsset = publication?.document_animation?.highlights;
    const ctasAsset = Object.values(publication?.assets ?? {}).find(
      (x) => x.asset_type === 'CallToActions',
    );

    if (cuepointAsset?.path && showDocumentAnimation) {
      const cuepoints = await fetchJsonAsset<{
        data: Cuepoint[];
      }>(cuepointAsset.path);
      console.debug('init', { cuepoints });
      if (cuepoints) {
        const transformedCuepoints = transformKeysToCamelCase(
          cuepoints.data,
        ) as Cuepoint[];
        setCuepoints(transformedCuepoints);
      }
    }

    if (highlightAsset?.path) {
      const highlightsAssetObject = await fetchJsonAsset<
        LegacyHighlightsAsset | NewHighlightsAsset
      >(highlightAsset.path);
      // console.debug('init', { highlightsAssetObject });
      // legacy model : nothing to do
      if (highlightsAssetObject && 'data' in highlightsAssetObject) {
        setHighlights(transformKeysToCamelCase(highlightsAssetObject.data));
      } else if (
        highlightsAssetObject &&
        'section_highlights' in highlightsAssetObject
      ) {
        // convert new model to legacy model to reuse app logic
        const { highlights, cuepoints } = sourcesToHighlightsAndCuepoints(
          highlightsAssetObject.section_highlights,
          highlightsAssetObject.document_id,
        );
        // console.debug('converted', { highlights, cuepoints });
        setHighlights(transformKeysToCamelCase(highlights?.data));
        setCuepoints(transformKeysToCamelCase(cuepoints?.data));

        const extractedSections = extractSectionsFromHighlightAsset(
          highlightsAssetObject.section_map,
          highlightsAssetObject.section_highlights,
          highlightsAssetObject.section_timestamps,
        );
        setSections(extractedSections);
      }
    }

    if (ctasAsset?.path) {
      const ctas = await fetchJsonAsset<CallToActions>(ctasAsset.path);
      console.debug('init', { ctas });
      if (ctas) {
        setCallToActions(ctas);
      }
    }

    return true;
  }, [publication, showDocumentAnimation]);

  const cleanHighlightAndCuepoints = useCallback(() => {
    setHighlights((old) => {
      return old?.filter((item) => item.id.indexOf('source-') < 0);
    });
    setCuepoints((old) => {
      return old?.filter((item) => item.highlightId.indexOf('source-') < 0);
    });
  }, []);

  return {
    cuepoints,
    setCuepoints,
    highlights,
    setHighlights,
    sections,
    setSections,
    callToActions,
    setCallToActions,
    loadAssets,
    cleanHighlightAndCuepoints,
  };
};
